/**
 * Sunucuda üretilen rapor çıktısını kaydetmeden önce gerçekten bir çalışma
 * kitabı olduğunu doğrular. Tek uygulama burasıdır.
 *
 * ⚠ Bu dosyanın başlığında eskiden "durum yoklama yardımcısı kaldırıldı" yazıyordu;
 * doğru değil. Yoklama yardımcısı (`sunucuExcelYokla`) hiç bu dosyada olmadı —
 * `sunucuExcelYoklama.ts`'te duruyor ve RaporDetayView onu kullanmaya devam ediyor
 * (servis yolu 5 sn, eski yol 3 sn aralık). İndirme de tek yerden yapılmıyor: zil
 * bildirimi (BildirimInfo), rapor ekranı (RaporDetayView) ve Excel Çıktıları ekranı.
 * [dev-3350] inceleme yorumu 881813371.
 *
 * Aynı nedenden `sunucuExcelYoklama.ts`'te bir kopyası yaşıyordu; o kopya silindi,
 * orası artık yalnız re-export. Kopyada iki kusur vardı: `onloadend` + `onerror`
 * ikisi birlikte okuma hatasında mesajı iki kez gösteriyordu ve `revokeObjectURL`
 * `click()` daha devreye girmeden çağrılıyordu.
 */


const INDIRME_HATA_METNI = 'Excel dosyasına ulaşılamadı. Lütfen yöneticinize bildirin.';

/**
 * Object URL'in serbest bırakılmadan önce bekleyeceği süre (ms).
 *
 * `click()` indirmeyi eşzamanlı tamamlamaz; tarayıcı (özellikle Safari) blob'u
 * navigasyon sırasında okur. URL'i 0 ms'de iptal etmek büyük dosyada indirmenin
 * yarıda kalmasına yol açabilir. FileSaver.js de aynı nedenle URL'i 40 sn sonra
 * iptal eder; aynı emsal alınmıştır.
 */
export const OBJECT_URL_SERBEST_BIRAKMA_GECIKMESI_MS = 40000;

/**
 * DEV-3396 (TDT-3483) — sunucudan inen gövdeyi ancak GERÇEKTEN elektronik tablo
 * ise kaydeder.
 *
 * Önceden iki çağrı yeri de gelen ne olursa olsun `.xlsx` adıyla kaydediyordu.
 * Sunucu hata gövdesi döndürdüğünde (durum "hazır" ama dosya okunamıyor) kullanıcı
 * 155 baytlık bir JSON'u "Excel" diye indiriyor ve açamıyordu; arızanın sunucuda
 * olduğu hiçbir yerde görünmüyordu.
 *
 * Durum koduna BAKILAMAZ: `$http` yanıt gövdesini döndürüyor (axios eklentisindeki
 * interceptor `response.data` veriyor), yani çağrı yerinde HTTP durumu yok. Bu
 * yüzden denetim gövdenin kendisinden yapılır.
 *
 * Ölçüt imza baytıdır, MIME tahmini değil: xlsx bir ZIP kabıdır ve daima `PK`
 * (0x50 0x4B) ile başlar. JSON/HTML hata gövdeleri başlamaz. MIME'a bakmak
 * güvenilmezdi — sunucu `application/octet-stream` de gönderebiliyor, hata
 * gövdesi de tipsiz gelebiliyor.
 */
export function sunucuExcelKaydet(
  govde: Blob,
  dosyaAdi: string,
  hata: (mesaj: string) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    const okuyucu = new FileReader();
    // `loadend` hata sonrasında da çalışır; `load` kullanmak hata callback'inin
    // iki kez çağrılmasını engeller.
    okuyucu.onload = () => {
      const bas = new Uint8Array(okuyucu.result as ArrayBuffer);
      const zipMi = bas.length >= 2 && bas[0] === 0x50 && bas[1] === 0x4b;
      if (!zipMi) {
        hata(INDIRME_HATA_METNI);
        resolve(false);
        return;
      }
      const url = window.URL.createObjectURL(govde);
      const a = document.createElement('a');
      a.href = url;
      a.download = dosyaAdi;
      // Safari dahil tarayicilar indirme navigasyonunu devralana kadar eleman ve
      // object URL yasamaya devam etsin (FileSaver.js emsali: 40 sn). Buyuk Blob'un
      // ikinci kopyasi uretilmez.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), OBJECT_URL_SERBEST_BIRAKMA_GECIKMESI_MS);
      resolve(true);
    };
    // Hata gövdesi de okunamazsa yine kaydetme; sessizce bozuk dosya inmesin.
    okuyucu.onerror = () => {
      hata(INDIRME_HATA_METNI);
      resolve(false);
    };
    okuyucu.readAsArrayBuffer(govde.slice(0, 2));
  });
}
